import { useQueries } from '@tanstack/react-query';
import { formatMoney } from '@ownermode/ui';
import { useState } from 'react';
import { ADVANCEMENT_MODE_COPY, LEAGUE_SIZE_COPY } from '@ownermode/shared';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router';
import { FeatureTutorial } from '../components/Tutorial';
import {
  Badge,
  Delta,
  Empty,
  ErrorState,
  LinkButton,
  Loading,
  Money,
  Section,
  Select,
  Skeleton,
  useToast,
} from '../components/kit';
import { Button, Field } from '../components/ui';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useChecklistVisit, useTutorial } from '../lib/tutorials';
import { keys, useAction, useMyLeagues, useRules, useSports } from '../lib/queries';
import type { Decision, Finances, InvitePreview, MyLeague, Sport } from '../lib/types';
import { InboxList } from './league/LeagueInfo';
import { PHASE_LABEL } from './league/context';

const SPORT_ART: Record<Sport, { emoji: string; blurb: string }> = {
  football: {
    emoji: '🏈',
    blurb: 'Hard cap, big stadiums, an eight-week sprint to the playoffs and a draft.',
  },
  basketball: {
    emoji: '🏀',
    blurb: 'Star-driven rosters, arena revenue and fierce cap trade-offs.',
  },
  soccer: {
    emoji: '⚽',
    blurb: 'Wage budgets, transfer windows and a fourteen-matchday league table.',
  },
};

/* ------------------------------------------------------------------ dashboard */

/** Owner's office: the whole portfolio, its value, and everything waiting on a decision. */
export function Dashboard() {
  const { user } = useAuth();
  const leagues = useMyLeagues();
  const owned = (leagues.data?.leagues ?? []).filter((l) => l.team);
  const finances = useQueries({
    queries: owned.map((l) => ({
      queryKey: keys.team(l.id, l.team!.id, 'finances'),
      queryFn: () => api<Finances>(`/api/teams/${l.team!.id}/finances`),
    })),
  });
  const decisions = useQueries({
    queries: owned.map((l) => ({
      queryKey: keys.team(l.id, l.team!.id, 'decisions'),
      queryFn: () => api<{ decisions: Decision[] }>(`/api/teams/${l.team!.id}/decisions`),
    })),
  });
  const welcome = useTutorial('welcome');
  if (!user) return null;
  // New owners (no franchise, never finished or skipped the welcome) start with the introduction;
  // an interrupted welcome resumes. Owners with a franchise are never redirected.
  if (leagues.isSuccess && owned.length === 0 && welcome.offer)
    return <Navigate to="/welcome" replace />;
  // A brand-new owner waits for their progress rather than seeing the dashboard flash first.
  const deciding = leagues.isSuccess && owned.length === 0 && !welcome.loaded && !welcome.failed;
  const portfolio = finances.reduce((s, f) => s + (f.data?.valuation ?? 0), 0);
  const cash = finances.reduce((s, f) => s + (f.data?.cash ?? 0), 0);
  const pending = decisions.flatMap((d, i) =>
    (d.data?.decisions ?? []).map((x) => ({ ...x, league: owned[i]! })),
  );

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
      <p className="text-xs uppercase tracking-[0.2em] text-gold-text">Executive office</p>
      <h1 className="mt-2 text-4xl">Good to see you, {user.displayName}.</h1>
      {leagues.isLoading || deciding ? (
        <div className="mt-8 grid gap-5 md:grid-cols-3" aria-busy="true">
          <Skeleton className="h-36" />
          <Skeleton className="h-36" />
          <Skeleton className="h-36" />
        </div>
      ) : leagues.error ? (
        <div className="mt-8">
          <ErrorState error={leagues.error} retry={() => void leagues.refetch()} />
        </div>
      ) : owned.length === 0 ? (
        <Onboarding hasLeagues={(leagues.data?.leagues.length ?? 0) > 0} />
      ) : (
        <>
          <div className="mt-8">
            <FeatureTutorial id="empire" />
          </div>
          <section className="panel relative overflow-hidden p-6 sm:p-8" aria-label="Portfolio">
            <div className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full bg-gold/10 blur-3xl" />
            <p className="text-xs uppercase tracking-[0.2em] text-slate">Portfolio value</p>
            <p
              className="mt-2 font-display text-5xl text-gold-bright sm:text-6xl"
              data-testid="portfolio-value"
            >
              {formatMoney(portfolio)}
            </p>
            <p className="mt-2 text-silver">
              {owned.length} franchise{owned.length === 1 ? '' : 's'} · <Money value={cash} /> cash
              across your clubs
            </p>
          </section>
          <div className="mt-6 grid gap-6 lg:grid-cols-[1.4fr_1fr]">
            <Section
              title="Your franchises"
              action={
                <LinkButton to="/leagues/new" variant="outline">
                  New league
                </LinkButton>
              }
            >
              <ul className="space-y-3">
                {owned.map((l, i) => {
                  const f = finances[i]?.data;
                  const last = f?.valuationHistory.at(-1);
                  return (
                    <li key={l.id}>
                      <Link
                        to={`/leagues/${l.id}`}
                        className="block rounded-2xl border border-overlay/8 bg-overlay/[0.02] p-4 transition hover:border-gold/40"
                      >
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div>
                            <p className="text-xs text-slate">
                              {SPORT_ART[l.sportId].emoji} {l.name} ·{' '}
                              {PHASE_LABEL[l.currentPhase] ?? l.currentPhase}
                            </p>
                            <p className="font-display text-xl text-paper">
                              {l.team!.city} {l.team!.name}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-display text-xl">
                              <Money value={f?.valuation} />
                            </p>
                            {last ? (
                              <p className="text-sm">
                                <Delta bps={last.changeBps} />
                              </p>
                            ) : null}
                          </div>
                        </div>
                        {last ? (
                          <p className="mt-2 line-clamp-2 text-sm text-slate">{last.explanation}</p>
                        ) : null}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </Section>
            <Section title="Needs your decision">
              {decisions.some((d) => d.isLoading) ? (
                <Skeleton />
              ) : pending.length === 0 ? (
                <Empty title="Nothing pending">
                  Your general managers have everything in hand.
                </Empty>
              ) : (
                <ul className="space-y-2" data-testid="dashboard-decisions">
                  {pending.slice(0, 8).map((d, i) => (
                    <li key={i}>
                      <Link
                        to={`/leagues/${d.league.id}/${d.path}`}
                        className="flex items-start justify-between gap-3 rounded-xl border border-overlay/5 p-3 hover:border-gold/30"
                      >
                        <span>
                          <span className="block text-paper">{d.title}</span>
                          <span className="block text-xs text-slate">
                            {d.league.team!.city} {d.league.team!.name} · {d.detail}
                          </span>
                        </span>
                        {d.urgent ? <Badge tone="amber">Now</Badge> : null}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </Section>
          </div>
        </>
      )}
    </div>
  );
}

function Onboarding({ hasLeagues }: { hasLeagues: boolean }) {
  return (
    <div className="mt-8 space-y-6">
      <section className="panel relative overflow-hidden p-8">
        <div className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full bg-gold/10 blur-3xl" />
        <p className="text-xs uppercase tracking-[0.2em] text-gold-text">Step one</p>
        <h2 className="mt-2 text-3xl">Buy your first franchise.</h2>
        <p className="mt-3 max-w-2xl text-silver">
          Pick a sport, found a league (alone against seven CPU ownership groups, or privately with
          friends), then take over a club. You'll run its roster, its {''}stadium, its sponsors and
          its balance sheet — and watch its valuation move.
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <LinkButton to="/leagues/new">Found a league</LinkButton>
          <LinkButton to="/join" variant="outline">
            I have an invite code
          </LinkButton>
        </div>
      </section>
      {hasLeagues ? (
        <p className="text-sm text-slate">
          You belong to a league but haven't chosen a franchise yet — open{' '}
          <Link className="text-gold-text" to="/leagues">
            My leagues
          </Link>
          .
        </p>
      ) : null}
      <div className="grid gap-4 md:grid-cols-3">
        {(Object.keys(SPORT_ART) as Sport[]).map((s) => (
          <div key={s} className="panel p-5">
            <p className="text-3xl" aria-hidden="true">
              {SPORT_ART[s].emoji}
            </p>
            <p className="mt-2 font-display text-xl capitalize">{s}</p>
            <p className="mt-1 text-sm text-silver">{SPORT_ART[s].blurb}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ my leagues */

export function MyLeagues() {
  const leagues = useMyLeagues();
  const status = (l: MyLeague) =>
    l.status === 'lobby' ? (
      <Badge tone="blue">Lobby</Badge>
    ) : l.status === 'paused' ? (
      <Badge tone="amber">Paused</Badge>
    ) : (
      <Badge tone="green">{PHASE_LABEL[l.currentPhase] ?? l.currentPhase}</Badge>
    );
  return (
    <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-gold-text">League office</p>
          <h1 className="mt-2 text-4xl">My leagues</h1>
        </div>
        <div className="flex gap-2">
          <LinkButton to="/join" variant="outline">
            Join with code
          </LinkButton>
          <LinkButton to="/leagues/new">Found a league</LinkButton>
        </div>
      </div>
      <div className="mt-8">
        {leagues.isLoading ? (
          <Loading />
        ) : leagues.error ? (
          <ErrorState error={leagues.error} />
        ) : leagues.data!.leagues.length === 0 ? (
          <Empty
            title="No leagues yet"
            action={<LinkButton to="/leagues/new">Found your first league</LinkButton>}
          >
            Found one, or join a friend's with their invite code.
          </Empty>
        ) : (
          <ul className="grid gap-4 sm:grid-cols-2">
            {leagues.data!.leagues.map((l) => (
              <li key={l.id}>
                <Link
                  to={`/leagues/${l.id}`}
                  className="panel block p-5 transition hover:border-gold/40"
                >
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-2xl" aria-hidden="true">
                      {SPORT_ART[l.sportId].emoji}
                    </p>
                    {status(l)}
                  </div>
                  <p className="mt-2 font-display text-xl text-paper">{l.name}</p>
                  <p className="text-sm text-slate">
                    {l.type === 'solo' ? 'Solo' : 'Private'} ·{' '}
                    {l.role === 'commissioner' ? 'Commissioner' : 'Owner'} ·{' '}
                    {l.team ? `${l.team.city} ${l.team.name}` : 'No franchise chosen'}
                  </p>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ create */

export function CreateLeague() {
  const sports = useSports();
  const navigate = useNavigate();
  const toast = useToast();
  const [step, setStep] = useState(0);
  const [sport, setSport] = useState<Sport | null>(null);
  const [type, setType] = useState<'solo' | 'private'>('solo');
  const [mode, setMode] = useState<'manual' | 'live' | 'async'>('manual');
  const [owners, setOwners] = useState(4);
  const [name, setName] = useState('');
  const rules = useRules(sport ?? undefined);
  const create = useAction(
    () =>
      api<{ leagueId: string }>('/api/leagues', {
        body: {
          name: name.trim(),
          sport,
          type,
          advancementMode: type === 'solo' ? mode : mode,
          maxHumanOwners: type === 'solo' ? 1 : owners,
        },
      }),
    [keys.mine],
  );
  const steps = ['Sport', 'Format', 'Review'];
  return (
    <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6">
      <p className="text-xs uppercase tracking-[0.2em] text-gold-text">Found a league</p>
      <h1 className="mt-2 text-4xl">
        {['Choose your sport', 'How will you play?', 'Name it and review the rules'][step]}
      </h1>
      <ol className="mt-5 flex gap-2 text-xs" aria-label="Progress">
        {steps.map((s, i) => (
          <li
            key={s}
            className={`rounded-full px-3 py-1 ${i === step ? 'bg-gold text-ink' : i < step ? 'bg-gold/20 text-gold-bright' : 'bg-overlay/5 text-slate'}`}
            aria-current={i === step ? 'step' : undefined}
          >
            {i + 1}. {s}
          </li>
        ))}
      </ol>
      <div className="mt-8">
        {sports.error ? (
          <ErrorState error={sports.error} />
        ) : step === 0 ? (
          sports.isLoading ? (
            <Loading />
          ) : (
            <>
              <p className="mb-4 text-sm text-silver">
                Each sport runs on its own rules. You can own franchises in all three, one per
                league.
              </p>
              <div className="grid gap-4 md:grid-cols-3">
                {sports.data!.sports.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => {
                      setSport(s.id);
                      setStep(1);
                    }}
                    className={`panel p-6 text-left transition hover:border-gold/50 ${sport === s.id ? 'border-gold/60' : ''}`}
                    aria-label={`Choose ${s.name}`}
                  >
                    <p className="text-4xl" aria-hidden="true">
                      {SPORT_ART[s.id].emoji}
                    </p>
                    <p className="mt-3 font-display text-2xl">{s.name}</p>
                    <p className="mt-2 text-sm text-silver">{SPORT_ART[s.id].blurb}</p>
                    <p className="mt-3 text-xs text-slate">
                      {s.financialModel === 'salary_cap' ? 'Salary cap' : 'Wage budget'} ·{' '}
                      {s.seasonStructure.regularSeasonRounds} {s.terminology.round.toLowerCase()}s ·{' '}
                      {s.terminology.venue}s
                    </p>
                  </button>
                ))}
              </div>
            </>
          )
        ) : step === 1 ? (
          <div className="space-y-6">
            <p className="text-sm text-silver" data-testid="league-size-hint">
              {LEAGUE_SIZE_COPY}
            </p>
            <div className="grid gap-4 md:grid-cols-2">
              {(['solo', 'private'] as const).map((t) => (
                <button
                  key={t}
                  onClick={() => setType(t)}
                  aria-pressed={type === t}
                  className={`panel p-6 text-left transition ${type === t ? 'border-gold/60 bg-gold/5' : 'hover:border-overlay/20'}`}
                >
                  <p className="font-display text-2xl">
                    {t === 'solo' ? 'Solo' : 'Private multiplayer'}
                  </p>
                  <p className="mt-2 text-sm text-silver">
                    {t === 'solo'
                      ? 'You against seven CPU ownership groups, each with its own personality. Play at your own pace.'
                      : 'Invite friends with a code. Unclaimed clubs stay CPU-run. You are the commissioner.'}
                  </p>
                </button>
              ))}
            </div>
            <div className="panel grid gap-4 p-6 sm:grid-cols-2">
              <Select
                label="How the league advances"
                value={mode}
                onChange={(e) => setMode(e.target.value as typeof mode)}
              >
                {(['manual', 'live', 'async'] as const).map((m) => (
                  <option key={m} value={m}>
                    {ADVANCEMENT_MODE_COPY[m].label}
                  </option>
                ))}
              </Select>
              {type === 'private' ? (
                <Select
                  label="Human owner seats"
                  value={owners}
                  onChange={(e) => setOwners(Number(e.target.value))}
                >
                  {[2, 3, 4, 5, 6, 7, 8].map((n) => (
                    <option key={n} value={n}>
                      {n} owners
                    </option>
                  ))}
                </Select>
              ) : null}
              <p
                className="text-sm text-silver sm:col-span-2"
                data-testid="mode-hint"
                aria-live="polite"
              >
                <span className="text-paper">{ADVANCEMENT_MODE_COPY[mode].label}:</span>{' '}
                {ADVANCEMENT_MODE_COPY[mode].short}
              </p>
            </div>
            <div className="flex justify-between">
              <Button variant="ghost" onClick={() => setStep(0)}>
                Back
              </Button>
              <Button onClick={() => setStep(2)}>Continue</Button>
            </div>
          </div>
        ) : (
          <form
            className="space-y-6"
            onSubmit={(e) => {
              e.preventDefault();
              create.mutate(undefined, {
                onSuccess: (r) => {
                  toast.success('League founded. Now choose your franchise.');
                  navigate(`/leagues/${r.leagueId}`);
                },
                onError: toast.error,
              });
            }}
          >
            <div className="panel p-6">
              <Field
                label="League name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                minLength={3}
                maxLength={60}
                required
                placeholder="e.g. Harbor Gridiron League"
              />
              <p className="mt-2 text-xs text-slate">
                The name appears in standings, invites and The Owner's Daily. Next you'll choose
                your franchise.
              </p>
            </div>
            {rules.data ? (
              <div className="panel p-6 text-sm text-silver">
                <p className="font-display text-lg text-paper">The rules you're signing up for</p>
                <ul className="mt-3 list-disc space-y-1.5 pl-5">
                  <li>
                    8 franchises; {rules.data.season.regularSeasonRounds}{' '}
                    {rules.data.terminology.round.toLowerCase()}s
                    {rules.data.season.format === 'playoffs'
                      ? `, then a ${rules.data.season.playoffTeams}-team playoff`
                      : ' in a league table'}
                    .
                  </li>
                  <li>
                    {rules.data.finance.model === 'salary_cap'
                      ? `Hard cap ${formatMoney(rules.data.finance.salaryCap ?? 0)}`
                      : `Wage budget = ${(rules.data.finance.wageBudgetRevenueShareBps ?? 0) / 100}% of revenue, plus a transfer budget`}
                    ; salaries {formatMoney(rules.data.finance.minSalary)}–
                    {formatMoney(rules.data.finance.maxSalary)}.
                  </li>
                  <li>
                    Rosters of {rules.data.roster.min}–{rules.data.roster.max}.{' '}
                    {rules.data.contracts.usesDraft
                      ? 'An annual draft and trades.'
                      : 'Transfer windows instead of trades.'}
                  </li>
                  <li>
                    Every round books real revenue and costs; your franchise valuation updates with
                    an explanation.
                  </li>
                  <li>
                    {type === 'solo' ? 'Solo' : `Private, up to ${owners} owners`} · {mode}{' '}
                    advancement.
                  </li>
                </ul>
              </div>
            ) : null}
            <div className="flex justify-between">
              <Button type="button" variant="ghost" onClick={() => setStep(1)}>
                Back
              </Button>
              <Button type="submit" disabled={create.isPending || name.trim().length < 3}>
                {create.isPending ? 'Founding…' : 'Found the league'}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ join */

export function JoinLeague() {
  const [params] = useSearchParams();
  const [code, setCode] = useState(params.get('code') ?? '');
  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [teamId, setTeamId] = useState('');
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();
  const lookup = async () => {
    setError(null);
    setBusy(true);
    try {
      const p = await api<InvitePreview>(
        `/api/invites/${encodeURIComponent(code.trim().toUpperCase())}`,
      );
      setPreview(p);
      setTeamId(p.availableTeams[0]?.id ?? '');
    } catch (e) {
      setError(
        e instanceof ApiError && e.code === 'NOT_FOUND'
          ? new Error('No open league matches that code.')
          : e,
      );
      setPreview(null);
    } finally {
      setBusy(false);
    }
  };
  const join = async () => {
    if (!preview) return;
    setBusy(true);
    try {
      await api(`/api/leagues/${preview.leagueId}/claim`, {
        body: { teamId, inviteCode: code.trim().toUpperCase() },
      });
      toast.success('Welcome to the league.');
      navigate(`/leagues/${preview.leagueId}`);
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(false);
    }
  };
  const rules = useRules(preview?.sport);
  return (
    <div className="mx-auto max-w-xl px-4 py-12">
      <p className="text-xs uppercase tracking-[0.2em] text-gold-text">Join a league</p>
      <h1 className="mt-2 text-4xl">Enter your invite code</h1>
      <FeatureTutorial id="join" />
      <form
        className="panel space-y-4 p-6"
        onSubmit={(e) => {
          e.preventDefault();
          void lookup();
        }}
      >
        <Field
          label="Invite code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          required
          maxLength={16}
          autoCapitalize="characters"
        />
        <Button type="submit" disabled={busy || !code.trim()}>
          Look up league
        </Button>
        {error ? <ErrorState error={error} /> : null}
      </form>
      {preview ? (
        <div className="panel mt-6 space-y-4 p-6">
          <p className="font-display text-2xl">{preview.name}</p>
          <p className="text-sm text-silver">
            {SPORT_ART[preview.sport].emoji} {preview.sport} · {preview.openSeats} open seat
            {preview.openSeats === 1 ? '' : 's'} · {preview.status}
          </p>
          {rules.data ? (
            <p className="text-xs text-slate">
              {rules.data.finance.model === 'salary_cap'
                ? 'Salary-cap league'
                : 'Wage-budget league with transfer windows'}
              .
            </p>
          ) : null}
          {preview.openSeats === 0 || preview.availableTeams.length === 0 ? (
            <p className="text-warning">This league is full.</p>
          ) : (
            <>
              <Select label="Franchise" value={teamId} onChange={(e) => setTeamId(e.target.value)}>
                {preview.availableTeams.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} (market {t.marketSize}/5)
                  </option>
                ))}
              </Select>
              <Button onClick={() => void join()} disabled={busy || !teamId}>
                Take over this franchise
              </Button>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}

export function InboxPage() {
  useChecklistVisit('open_inbox');
  return (
    <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6">
      <p className="text-xs uppercase tracking-[0.2em] text-gold-text">Correspondence</p>
      <h1 className="mt-2 mb-6 text-4xl">Inbox</h1>
      <InboxList />
    </div>
  );
}
